import { groupBy } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterChips from 'src/components/filter-chips/filter-chips';
import Popup from 'src/components/form/search/filters-popup/popup';
import PageHeader from 'src/components/page-header/page-header';
import PaginatedFooter from 'src/components/paginated-footer/paginated-footer';
import { ChipsFilter, ChipsType } from 'src/types';
import { EventFilters, EventListItem, GroupedEvents, LocationOption } from 'src/types/events';
import API from './data/events-api';
import { useGetEventsLocations } from './data/queries';
import css from './events.module.scss';
import EventsFilters from './form/filters';
import SearchForm from './form/form';
import DailyGroup from './list/daily-group';
import EmptyPlaceholder from './list/empty-placeholder';
import { useResize } from 'src/hooks/shared-hooks';

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams?.get('search') ?? '';
  const currentDate = useRef(new Date());
  const selectedEvent = document?.getElementById(`${searchParams?.get('slug')}`) ?? null;
  const [selectedPage, setSelectedPage] = useState(1);
  const [filterValues, setFilterValues] = useState<EventFilters>({
    search: searchValue ?? '',
    locationId: [],
    dateFrom: currentDate.current.getTime(),
    page: 1,
    limit: 12
  });

  const dimensions = useResize();
  const mobileMode = dimensions.width <= 500;
  const events = useEvents(selectedPage, filterValues);
  const { data: locations = [] } = useGetEventsLocations();

  const selectedFilters: ChipsFilter[] = locations
    .filter((lo: LocationOption) => filterValues.locationId.includes(lo.id))
    .map((f: LocationOption) => ({ id: f.id, name: f.name, type: ChipsType.location }));

  const hidePaginatedFooter = events.count === 0 || events.totalCount === 0 || events.count === events.totalCount;

  function handleFiltersChange(data: Partial<EventFilters>) {
    const trimmedText = data.search ? data.search?.trim() : '';
    const nextSearchParams = new URLSearchParams(searchParams);
    trimmedText?.length > 0 ? nextSearchParams.set('search', trimmedText) : nextSearchParams.delete('search');

    data.locationId && data.locationId.length > 0
      ? nextSearchParams.set('location', data.locationId.join('&'))
      : nextSearchParams.delete('locationId');

    if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);

    setFilterValues({ ...filterValues, ...data });
    setSelectedPage(1);
  }

  const setSearchUrl = useCallback(
    (items: { name: string; value: string | null | undefined }[]) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      for (const item of items) {
        item.value && item.value.length > 0
          ? nextSearchParams.set(item.name, item.value)
          : nextSearchParams.delete(item.name);
      }

      if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = () => {
    setFilterValues({ ...filterValues, search: '', locationId: [], dateFrom: currentDate.current.getTime() });
  };

  const deselectFilter = (filter: ChipsFilter) => {
    if (filter.type === ChipsType.location) {
      setFilterValues({
        ...filterValues,
        locationId: filterValues.locationId.filter((lid: string) => lid !== filter.id)
      });
    }
  };

  useEffect(() => {
    const location: string = locations
      ?.filter((loc: LocationOption) => filterValues.locationId.includes(loc.id))
      ?.map((loc: LocationOption) => loc.name)
      ?.join('&');

    const valuesForParams: { [key: string]: string | null } = { search: filterValues.search, location };
    const search = Object.keys(valuesForParams).map((key: string) => {
      return { name: key, value: valuesForParams[key]?.toString() };
    });
    setSearchUrl(search);
  }, [locations, filterValues, setSearchUrl]);

  useEffect(() => {
    setSearchUrl([{ name: 'slug', value: null }]);
    if (selectedEvent) {
      selectedEvent?.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }, [selectedEvent, setSearchUrl]);

  return (
    <main className={css.events}>
      <PageHeader title='Події' description='Заходи для захисників, захисниць та їхніх родин' />
      <div className={css.body}>
        <div className={css.filters}>
          <SearchForm initialValues={{ search: filterValues.search }} onSubmit={handleFiltersChange} />
          {mobileMode && (
            <Popup onReset={clearFilters}>
              <EventsFilters
                initialValues={filterValues}
                locations={locations}
                onSubmit={handleFiltersChange}
                currentDate={currentDate.current}
              />
            </Popup>
          )}
        </div>

        <div className={css.eventsContainer}>
          {!mobileMode && (
            <EventsFilters
              initialValues={filterValues}
              locations={locations}
              onSubmit={handleFiltersChange}
              currentDate={currentDate.current}
            />
          )}

          <div className={css.list}>
            {selectedFilters?.length > 0 && (
              <FilterChips selectedFilters={selectedFilters} onClick={deselectFilter} onClear={clearFilters} />
            )}
            {events.count === 0 && <EmptyPlaceholder />}
            {events.count !== 0 &&
              Object.keys(events.items).map((date) => (
                <DailyGroup currentDate={currentDate.current} groups={events.items[date]} groupKey={date} key={date} />
              ))}
            {!hidePaginatedFooter && (
              <div className={css.pagerRow}>
                <PaginatedFooter caption='Більше подій' onClick={() => setSelectedPage(selectedPage + 1)} />
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function useEvents(selectedPage: number, filters: EventFilters) {
  const [events, setEvents] = useState<GroupedEvents>({ count: 0, totalCount: 0, items: {} });

  useEffect(() => {
    const data = {
      search: filters.search,
      locationId: filters.locationId,
      dateFrom: filters.dateFrom
    };

    function getPreviousEvents(previousEvents: Record<string, EventListItem[]>) {
      const flattedPreviousEvents = Object.keys(previousEvents)
        .map((group) => previousEvents[group])
        .flat();

      return flattedPreviousEvents;
    }

    function groupByDayPredicate(event: EventListItem) {
      const scheduledAt = new Date(event.scheduledAt);
      return new Date(scheduledAt.getFullYear(), scheduledAt.getMonth(), scheduledAt.getDate());
    }

    API.getEvents(selectedPage, data).then((response) => {
      if (!response?.items || response.items.length === 0) {
        setEvents({ count: 0, totalCount: 0, items: {} });
        return;
      }

      setEvents((prev) => {
        if (selectedPage > 1) {
          const previousEvents = getPreviousEvents(prev.items);

          return {
            items: groupBy([...previousEvents, ...response.items], groupByDayPredicate),
            count: previousEvents.length + response.items.length,
            totalCount: response.totalCount
          };
        }

        return {
          items: groupBy(response.items, groupByDayPredicate),
          count: response.items.length,
          totalCount: response.totalCount
        };
      });
    });
  }, [selectedPage, filters]);

  return events;
}
